/**
 * API endpoints - 所有API调用函数
 */
import { apiClient } from './client';
import type {
  // Auth
  PhoneLoginRequest,
  SmsLoginRequest,
  SendCodeRequest,
  RegisterRequest,
  RefreshTokenRequest,
  LoginResponse,
  UserInfo,
  // Family
  FamilyInfo,
  FamilyCreate,
  FamilyUpdate,
  FamilyMemberInfo,
  // Child
  ChildInfo,
  ChildCreate,
  ChildUpdate,
  ChildLevelInfo,
  // Rules
  RewardRuleInfo,
  RewardRuleCreate,
  RewardRuleUpdate,
  PunishRuleInfo,
  PunishRuleCreate,
  PunishRuleUpdate,
  // Exchange
  ExchangeItemInfo,
  ExchangeItemCreate,
  ExchangeItemUpdate,
  ExchangeRequest,
  // Records
  PointRecordInfo,
  RecordRewardRequest,
  RecordPunishRequest,
  RecordRewardResponse,
  TodayCheckinItem,
  RecordsStats,
  // Dashboard
  DashboardResponse,
  // Bank
  BankAccountInfo,
  BankTransactionInfo,
  DepositRequest,
  WithdrawRequest,
  // Market
  IncentiveTemplateSimple,
  IncentiveTemplateDetail,
  TemplateCopyRequest,
  // AI
  AIChatRequest,
  AIChatResponse,
  // VIP
  VIPInfo,
  // Payment
  CreatePaymentRequest,
  CreatePaymentResponse,
  PaymentInfo,
  // Notification
  NotificationInfo,
  NotificationSettings,
  UpdateNotificationSettings,
  // Timer
  TimerTaskInfo,
  CreateTimerTaskRequest,
  UpdateTimerTaskRequest,
  // Assessment
  FamilyAssessment,
  ChildAssessment,
  ChildReport,
  // Settings
  ProfileUpdate,
  PasswordUpdateRequest,
  PhoneUpdateRequest,
} from './types';

// ========== 认证API ==========

export const authApi = {
  /** 手机号密码登录 */
  loginByPhone: (data: PhoneLoginRequest) =>
    apiClient.post<LoginResponse>('/auth/login/phone', data),

  /** 手机号验证码登录 */
  loginBySms: (data: SmsLoginRequest) =>
    apiClient.post<LoginResponse>('/auth/login/sms', data),

  /** 发送验证码 */
  sendCode: (data: SendCodeRequest) =>
    apiClient.post('/auth/send-code', data),

  /** 用户注册 */
  register: (data: RegisterRequest) =>
    apiClient.post<LoginResponse>('/auth/register', data),

  /** 刷新Token */
  refreshToken: (data: RefreshTokenRequest) =>
    apiClient.post<{ access_token: string; refresh_token: string }>('/auth/refresh', data),

  /** 退出登录 */
  logout: () =>
    apiClient.post('/auth/logout'),

  /** 获取当前用户信息 */
  getCurrentUser: () =>
    apiClient.get<UserInfo>('/auth/me'),
};

// ========== 家庭API ==========

export const familyApi = {
  /** 获取用户的家庭列表 */
  getMyFamilies: () =>
    apiClient.get<FamilyInfo[]>('/families'),

  /** 创建新家庭 */
  createFamily: (data: FamilyCreate) =>
    apiClient.post<FamilyInfo>('/families', data),

  /** 获取家庭详情 */
  getFamilyDetail: (familyId: string) =>
    apiClient.get<FamilyInfo>(`/families/${familyId}`),

  /** 更新家庭设置 */
  updateFamily: (familyId: string, data: FamilyUpdate) =>
    apiClient.put<FamilyInfo>(`/families/${familyId}`, data),

  /** 切换当前家庭 */
  switchFamily: (familyId: string) =>
    apiClient.post<FamilyInfo>(`/families/${familyId}/switch`),

  /** 获取家庭成员列表 */
  getFamilyMembers: (familyId: string) =>
    apiClient.get<FamilyMemberInfo[]>(`/families/${familyId}/members`),

  /** 获取儿童列表 */
  getChildren: (familyId: string) =>
    apiClient.get<ChildInfo[]>(`/families/${familyId}/children`),

  /** 添加儿童 */
  createChild: (familyId: string, data: ChildCreate) =>
    apiClient.post<ChildInfo>(`/families/${familyId}/children`, data),

  /** 获取儿童详情 */
  getChildDetail: (familyId: string, childId: string) =>
    apiClient.get<ChildInfo>(`/families/${familyId}/children/${childId}`),

  /** 更新儿童信息 */
  updateChild: (familyId: string, childId: string, data: ChildUpdate) =>
    apiClient.put<ChildInfo>(`/families/${familyId}/children/${childId}`, data),

  /** 删除儿童 */
  deleteChild: (familyId: string, childId: string) =>
    apiClient.delete(`/families/${familyId}/children/${childId}`),

  /** 获取等级列表 */
  getLevels: (familyId: string) =>
    apiClient.get<ChildLevelInfo[]>(`/families/${familyId}/levels`),

  /** 添加家庭成员 */
  addFamilyMember: (familyId: string, data: FamilyMemberCreate) =>
    apiClient.post<FamilyMemberInfo>(`/families/${familyId}/members`, data),

  /** 更新家庭成员 */
  updateFamilyMember: (familyId: string, memberId: string, data: FamilyMemberUpdate) =>
    apiClient.put<FamilyMemberInfo>(`/families/${familyId}/members/${memberId}`, data),

  /** 删除家庭成员 */
  deleteFamilyMember: (familyId: string, memberId: string) =>
    apiClient.delete(`/families/${familyId}/members/${memberId}`),
};

// ========== 奖惩规则API ==========

export const rulesApi = {
  /** 获取奖励规则列表 */
  getRewardRules: (familyId: string) =>
    apiClient.get<RewardRuleInfo[]>(`/families/${familyId}/reward-rules`),

  /** 创建奖励规则 */
  createRewardRule: (familyId: string, data: RewardRuleCreate) =>
    apiClient.post<RewardRuleInfo>(`/families/${familyId}/reward-rules`, data),

  /** 获取奖励规则详情 */
  getRewardRule: (familyId: string, ruleId: string) =>
    apiClient.get<RewardRuleInfo>(`/families/${familyId}/reward-rules/${ruleId}`),

  /** 更新奖励规则 */
  updateRewardRule: (familyId: string, ruleId: string, data: RewardRuleUpdate) =>
    apiClient.put<RewardRuleInfo>(`/families/${familyId}/reward-rules/${ruleId}`, data),

  /** 删除奖励规则 */
  deleteRewardRule: (familyId: string, ruleId: string) =>
    apiClient.delete(`/families/${familyId}/reward-rules/${ruleId}`),

  /** 获取惩罚规则列表 */
  getPunishRules: (familyId: string) =>
    apiClient.get<PunishRuleInfo[]>(`/families/${familyId}/punish-rules`),

  /** 创建惩罚规则 */
  createPunishRule: (familyId: string, data: PunishRuleCreate) =>
    apiClient.post<PunishRuleInfo>(`/families/${familyId}/punish-rules`, data),

  /** 获取惩罚规则详情 */
  getPunishRule: (familyId: string, ruleId: string) =>
    apiClient.get<PunishRuleInfo>(`/families/${familyId}/punish-rules/${ruleId}`),

  /** 更新惩罚规则 */
  updatePunishRule: (familyId: string, ruleId: string, data: PunishRuleUpdate) =>
    apiClient.put<PunishRuleInfo>(`/families/${familyId}/punish-rules/${ruleId}`, data),

  /** 删除惩罚规则 */
  deletePunishRule: (familyId: string, ruleId: string) =>
    apiClient.delete(`/families/${familyId}/punish-rules/${ruleId}`),
};

// ========== 积分商城API ==========

export const exchangeApi = {
  /** 获取商品列表 */
  getExchangeItems: (familyId: string) =>
    apiClient.get<ExchangeItemInfo[]>(`/families/${familyId}/exchange-items`),

  /** 添加商品 */
  createExchangeItem: (familyId: string, data: ExchangeItemCreate) =>
    apiClient.post<ExchangeItemInfo>(`/families/${familyId}/exchange-items`, data),

  /** 获取商品详情 */
  getExchangeItem: (familyId: string, itemId: string) =>
    apiClient.get<ExchangeItemInfo>(`/families/${familyId}/exchange-items/${itemId}`),

  /** 更新商品 */
  updateExchangeItem: (familyId: string, itemId: string, data: ExchangeItemUpdate) =>
    apiClient.put<ExchangeItemInfo>(`/families/${familyId}/exchange-items/${itemId}`, data),

  /** 删除商品 */
  deleteExchangeItem: (familyId: string, itemId: string) =>
    apiClient.delete(`/families/${familyId}/exchange-items/${itemId}`),

  /** 积分兑换 */
  exchangePoints: (familyId: string, data: ExchangeRequest) =>
    apiClient.post(`/families/${familyId}/exchange`, data),
};

// ========== 积分记录API ==========

export const recordsApi = {
  /** 记录奖励 */
  recordReward: (data: RecordRewardRequest) =>
    apiClient.post<RecordRewardResponse>('/records/reward', data),

  /** 记录惩罚 */
  recordPunish: (data: RecordPunishRequest) =>
    apiClient.post<RecordRewardResponse>('/records/punish', data),

  /** 获取积分记录列表 */
  getPointRecords: (
    familyId: string,
    params?: {
      child_id?: string;
      record_type?: number;
      start_date?: string;
      end_date?: string;
      page?: number;
      page_size?: number;
    }
  ) => apiClient.get<PointRecordInfo[]>(`/families/${familyId}/records`, params),

  /** 获取统计信息 */
  getRecordsStatistics: (familyId: string, child_id?: string) =>
    apiClient.get<RecordsStats>(`/families/${familyId}/records/statistics`, { child_id }),

  /** 获取今日打卡清单 */
  getTodayCheckins: (familyId: string) =>
    apiClient.get<TodayCheckinItem[]>(`/families/${familyId}/checkins/today`),

  /** 完成打卡 */
  completeCheckin: (checkinId: string) =>
    apiClient.post(`/checkins/${checkinId}/complete`),
};

// ========== 仪表盘API ==========

export const dashboardApi = {
  /** 获取仪表盘数据 */
  getDashboard: (familyId: string) =>
    apiClient.get<DashboardResponse>(`/families/${familyId}/dashboard`),
};

// ========== 家庭银行API ==========

export const bankApi = {
  /** 获取儿童银行账户 */
  getBankAccount: (childId: string) =>
    apiClient.get<BankAccountInfo>(`/bank/children/${childId}/account`),

  /** 存入 */
  deposit: (childId: string, data: DepositRequest) =>
    apiClient.post<BankAccountInfo>(`/bank/children/${childId}/deposit`, data),

  /** 取出 */
  withdraw: (childId: string, data: WithdrawRequest) =>
    apiClient.post<BankAccountInfo>(`/bank/children/${childId}/withdraw`, data),

  /** 获取交易记录 */
  getTransactions: (childId: string, transaction_type?: number) =>
    apiClient.get<BankTransactionInfo[]>(`/bank/children/${childId}/transactions`, { transaction_type }),

  /** 充值积分 - 用钱换积分 */
  rechargePoints: (childId: string, data: { amount: number }) =>
    apiClient.post(`/bank/children/${childId}/recharge-points`, data),

  /** 积分换钱 */
  exchangePointsToMoney: (childId: string, data: { points: number }) =>
    apiClient.post(`/bank/children/${childId}/exchange-points`, data),

  /** 获取利率设置 */
  getInterestRate: (familyId: string) =>
    apiClient.get<{ annual_rate: number }>(`/bank/families/${familyId}/interest-rate`),

  /** 设置利率 */
  setInterestRate: (familyId: string, rate: number) =>
    apiClient.put(`/bank/families/${familyId}/interest-rate`, { rate }),
};

// ========== 激励市场API ==========

export const marketApi = {
  /** 获取模板列表 */
  getTemplates: (params?: {
    keyword?: string;
    habit_type?: number;
    is_vip?: boolean;
  }) => apiClient.get<IncentiveTemplateSimple[]>('/market/templates', params),

  /** 获取模板详情 */
  getTemplate: (templateId: string) =>
    apiClient.get<IncentiveTemplateDetail>(`/market/templates/${templateId}`),

  /** 复制模板 */
  copyTemplate: (data: TemplateCopyRequest) =>
    apiClient.post<{ template_id: string }>(`/market/templates/${data.template_id}/copy`, { family_id: data.family_id }),
};

// ========== AI助手API ==========

export const aiApi = {
  /** AI对话 */
  chat: (data: AIChatRequest) =>
    apiClient.post<AIChatResponse>('/ai/chat', data),
  /** 获取AI使用次数和限制状态 */
  getUsage: (familyId: string) =>
    apiClient.get<{ is_vip: boolean; ai_used: number; ai_limit: number; is_exceeded: boolean }>('/ai/usage', { family_id: familyId }),
};

// ========== 会员中心API ==========

export const vipApi = {
  /** 获取会员信息 */
  getVipInfo: (familyId: string) =>
    apiClient.get<VIPInfo>(`/vip/families/${familyId}/info`),

  /** 获取会员权益 */
  getVipRights: () =>
    apiClient.get('/vip/rights'),
};

// ========== 支付API ==========

export const paymentApi = {
  /** 创建支付订单 */
  createPayment: (data: CreatePaymentRequest) =>
    apiClient.post<CreatePaymentResponse>('/payments/create', data),

  /** 获取支付历史 */
  getPaymentHistory: (familyId: string) =>
    apiClient.get<PaymentInfo[]>('/payments/history', { family_id: familyId }),
};

// ========== 消息通知API ==========

export const notificationApi = {
  /** 获取消息列表 */
  getNotifications: (is_read?: boolean) =>
    apiClient.get<NotificationInfo[]>('/notifications', { is_read }),

  /** 标记已读 */
  markRead: (id: string) =>
    apiClient.put(`/notifications/${id}/read`),

  /** 全部已读 */
  markAllRead: () =>
    apiClient.put('/notifications/read-all'),

  /** 获取通知设置 */
  getNotificationSettings: () =>
    apiClient.get<NotificationSettings>('/settings/notifications'),

  /** 更新通知设置 */
  updateNotificationSettings: (data: UpdateNotificationSettings) =>
    apiClient.put<NotificationSettings>('/settings/notifications', data),
};

// ========== 计时任务API ==========

export const timerApi = {
  /** 创建计时任务 */
  createTimerTask: (data: CreateTimerTaskRequest) =>
    apiClient.post<TimerTaskInfo>('/timer/tasks', data),

  /** 获取进行中的任务 */
  getActiveTasks: (familyId: string, child_id?: string) =>
    apiClient.get<TimerTaskInfo[]>(`/timer/families/${familyId}/tasks`, { child_id }),

  /** 获取计时任务 */
  getTask: (taskId: string) =>
    apiClient.get<TimerTaskInfo>(`/timer/tasks/${taskId}`),

  /** 更新计时任务 */
  updateTask: (taskId: string, data: UpdateTimerTaskRequest) =>
    apiClient.put<TimerTaskInfo>(`/timer/tasks/${taskId}`, data),
};

// ========== 习惯评估API ==========

export const assessmentApi = {
  /** 获取家庭评估 */
  getFamilyAssessment: (familyId: string, days?: number) =>
    apiClient.get<FamilyAssessment>(`/assessment/families/${familyId}`, { days }),

  /** 获取儿童评估 */
  getChildAssessment: (childId: string, days?: number) =>
    apiClient.get<ChildAssessment>(`/assessment/children/${childId}`, { days }),

  /** 获取儿童报告 */
  getChildReport: (childId: string, days?: number) =>
    apiClient.get<ChildReport>(`/assessment/children/${childId}/report`, { days }),
};

// ========== 系统设置API ==========

export const settingsApi = {
  /** 获取个人信息 */
  getProfile: () =>
    apiClient.get<UserInfo>('/settings/profile'),

  /** 更新个人信息 */
  updateProfile: (data: ProfileUpdate) =>
    apiClient.put<UserInfo>('/settings/profile', data),

  /** 修改密码 */
  updatePassword: (data: PasswordUpdateRequest) =>
    apiClient.put('/settings/password', data),

  /** 修改手机号 */
  updatePhone: (data: PhoneUpdateRequest) =>
    apiClient.put<UserInfo>('/settings/phone', data),

  /** 绑定微信 */
  bindWechat: () =>
    apiClient.post('/settings/wechat/bind'),

  /** 解除微信绑定 */
  unbindWechat: () =>
    apiClient.post('/settings/wechat/unbind'),

  /** 上传头像 */
  uploadAvatar: (imageBase64: string, filename: string) =>
    apiClient.post<{ url: string }>('/settings/upload/avatar', {
      image_base64: imageBase64,
      filename: filename,
    }),
};

// ========== API Key管理 ==========

export interface ApiKeyInfo {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface ApiKeyCreated extends ApiKeyInfo {
  key: string;  // 明文key，仅创建时返回
}

export const apiKeyApi = {
  /** 获取API Key列表 */
  listApiKeys: () =>
    apiClient.get<ApiKeyInfo[]>('/api-keys'),

  /** 创建API Key */
  createApiKey: (name: string = '默认Key') =>
    apiClient.post<ApiKeyCreated>('/api-keys', { name }),

  /** 删除API Key */
  deleteApiKey: (keyId: string) =>
    apiClient.delete(`/api-keys/${keyId}`),

  /** 获取AI模型配置 */
  getAiConfig: () =>
    apiClient.get('/api-keys/ai-config'),

  /** 更新AI模型配置 */
  updateAiConfig: (data: {
    provider?: string;
    model_name?: string;
    api_base_url?: string;
    api_key?: string;
    max_tokens?: number;
    temperature?: number;
  }) => apiClient.put('/api-keys/ai-config', data),
};

// 导出所有API
export const api = {
  auth: authApi,
  family: familyApi,
  rules: rulesApi,
  exchange: exchangeApi,
  records: recordsApi,
  dashboard: dashboardApi,
  bank: bankApi,
  market: marketApi,
  ai: aiApi,
  vip: vipApi,
  payment: paymentApi,
  notification: notificationApi,
  timer: timerApi,
  assessment: assessmentApi,
  settings: settingsApi,
};

export default api;
